import React, { useState } from 'react';
import { MapPin, Grid, Map as MapIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RevealSection, CategoryIcon, getDefaultImage, getManufacturerColor } from './shared';
import i18n from 'i18next';
const ProjectsMap = React.lazy(() => import('@/components/ProjectsMap'));

export const ProjectsSection = ({ sid, theme, txt, filteredProjects, projectCategories, selectedCategory, setSelectedCategory, setSelectedProject }) => {
  const [viewMode, setViewMode] = useState('grid');
  const [visibleProjects, setVisibleProjects] = useState(6);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const PROJECTS_PER_PAGE = 6;

  const getCategoryForProject = (slug) => projectCategories.find(c => c.slug === slug);

  const loadMoreProjects = () => {
    setIsLoadingMore(true);
    setTimeout(() => { setVisibleProjects(prev => prev + PROJECTS_PER_PAGE); setIsLoadingMore(false); }, 400);
  };

  // Reset visible count when category changes
  React.useEffect(() => { setVisibleProjects(PROJECTS_PER_PAGE); }, [selectedCategory]);

  return (
    <section id={sid} className="py-16 sm:py-28 bg-white" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <RevealSection className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-1.5 rounded-full text-sm font-medium" style={{ backgroundColor: `${theme.primary_color}10`, color: theme.primary_color }}>{txt('projects', 'badge')}</div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-slate-900 mb-4" style={{ fontFamily: `${theme.font_heading}, sans-serif` }}>{txt('projects', 'title')}</h2>
          <p className="text-sm sm:text-base md:text-lg text-slate-500 mb-8" style={{ fontFamily: `${theme.font_body}, sans-serif` }}>{txt('projects', 'subtitle')}</p>

          <div className="flex justify-center mb-6">
            <div className="inline-flex rounded-full bg-slate-100 p-1">
              <button onClick={() => setViewMode('grid')} className="flex items-center space-x-2 px-6 py-2 rounded-full transition-all duration-300" style={viewMode === 'grid' ? { backgroundColor: theme.primary_color, color: '#fff' } : { color: '#475569' }} data-testid="view-grid-button">
                <Grid className="w-4 h-4" /> <span>{txt('projects', 'view_grid')}</span>
              </button>
              <button onClick={() => setViewMode('map')} className="flex items-center space-x-2 px-6 py-2 rounded-full transition-all duration-300" style={viewMode === 'map' ? { backgroundColor: theme.primary_color, color: '#fff' } : { color: '#475569' }} data-testid="view-map-button">
                <MapIcon className="w-4 h-4" /> <span>{txt('projects', 'view_map')}</span>
              </button>
            </div>
          </div>

          <div className="flex flex-nowrap sm:flex-wrap justify-start sm:justify-center gap-2 sm:gap-3 overflow-x-auto pb-2 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide" data-testid="project-filters">
            <Button onClick={() => setSelectedCategory('all')} className="rounded-full px-6 transition-all duration-300" style={selectedCategory === 'all' ? { backgroundColor: theme.primary_color, color: '#fff', boxShadow: `0 4px 14px ${theme.primary_color}40` } : { backgroundColor: '#fff', color: '#475569', border: '1px solid #e2e8f0' }} data-testid="filter-all">
              {txt('projects', 'filter_all')}
            </Button>
            {projectCategories.map((cat) => (
              <Button key={cat.slug} onClick={() => setSelectedCategory(cat.slug)} className="rounded-full px-5 transition-all duration-300 flex items-center gap-2" style={selectedCategory === cat.slug ? { backgroundColor: cat.color || theme.primary_color, color: '#fff', boxShadow: `0 4px 14px ${cat.color || theme.primary_color}40` } : { backgroundColor: '#fff', color: '#475569', border: '1px solid #e2e8f0' }} data-testid={`filter-${cat.slug}`}>
                <CategoryIcon iconName={cat.icon} color={selectedCategory === cat.slug ? '#fff' : (cat.color || '#475569')} size={16} />
                <span className="hidden sm:inline">{(i18n.language || '').startsWith('en') && cat.name_en ? cat.name_en : cat.name_cs}</span>
              </Button>
            ))}
          </div>
        </RevealSection>

        {viewMode === 'map' ? (
          <RevealSection direction="scale">
            <React.Suspense fallback={<div className="h-[600px] bg-slate-100 rounded-2xl animate-pulse" />}>
              <ProjectsMap projects={filteredProjects} onProjectClick={setSelectedProject} categories={projectCategories} />
            </React.Suspense>
          </RevealSection>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.slice(0, visibleProjects).map((project, index) => (
                <Card key={project.id} className="card-lift overflow-hidden border border-slate-100 rounded-2xl cursor-pointer group bg-white shadow-md hover:shadow-2xl animate-fade-in-up" style={{ animationDelay: `${(index % PROJECTS_PER_PAGE) * 120}ms`, animationFillMode: 'both' }} onClick={() => setSelectedProject(project)} data-testid={`project-card-${index}`}>
                  <div className="relative h-60 overflow-hidden">
                    <img src={project.images[0] || getDefaultImage(project.category)} alt={project.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
                      {(() => { const cat = getCategoryForProject(project.category); return cat ? (
                        <div className="h-6 px-2.5 flex items-center gap-1 rounded-md text-[10px] font-bold text-white shadow-lg" style={{ backgroundColor: cat.color || '#f59e0b' }}>
                          <CategoryIcon iconName={cat.icon} color="#fff" size={12} />
                          <span className="hidden sm:inline">{(i18n.language || '').startsWith('en') && cat.name_en ? cat.name_en : cat.name_cs}</span>
                        </div>
                      ) : null; })()}
                      {project.manufacturer && (
                        <div className="h-6 px-3 flex items-center justify-center rounded-md text-[10px] font-bold uppercase tracking-wide text-white shadow-lg" style={{ background: getManufacturerColor(project.manufacturer) }}>{project.manufacturer}</div>
                      )}
                      {project.capacity_kwp && (
                        <div className="h-6 px-3 flex items-center justify-center rounded-md text-[10px] font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 shadow-lg"><span className="mr-1">⚡</span>{project.capacity_kwp} kWp</div>
                      )}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-slate-700 transition-colors" style={{ fontFamily: `${theme.font_heading}, sans-serif` }}>{project.title}</h3>
                    <div className="flex items-center text-slate-500 text-sm mb-3">
                      <MapPin className="w-4 h-4 mr-2" style={{ color: theme.primary_color }} />{project.location}
                    </div>
                    <p className="text-slate-500 line-clamp-2 text-sm" style={{ fontFamily: `${theme.font_body}, sans-serif` }}>{project.description}</p>
                  </div>
                </Card>
              ))}
            </div>
            {visibleProjects < filteredProjects.length && (
              <div className="flex justify-center mt-12">
                <Button onClick={loadMoreProjects} disabled={isLoadingMore} className="px-8 py-3 rounded-full text-white font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed" style={{ backgroundColor: theme.primary_color }} data-testid="load-more-projects-btn">
                  {isLoadingMore ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                      {txt('projects', 'loading')}
                    </span>
                  ) : (<>{txt('projects', 'load_more')} ({filteredProjects.length - visibleProjects} {txt('projects', 'remaining')})</>)}
                </Button>
              </div>
            )}
            <p className="text-center text-slate-500 mt-6 text-sm">
              {txt('projects', 'showing')} {Math.min(visibleProjects, filteredProjects.length)} {txt('projects', 'of')} {filteredProjects.length} {txt('projects', 'references')}
            </p>
          </>
        )}
      </div>
    </section>
  );
};
